import React, {FC, useEffect, useState} from "react"
import PropTypes from "prop-types"

import {Address, Checkout, Deal, PaymentCurrencyType, PaymentProvider, Wallet, WalletEntityType} from "@irati/types"
import {Row} from "@irati/ui/src/ColumnRow"
import redemptionFlow from "@irati/checkout/utils/redemption"
import tracking from "@irati-tracking/perks/perks"
import authentication from "@irati/auth"
import checkoutService from "@irati/checkout/services/checkout"
import PaymentSelect from "@irati-web/perks/src/components/DealPayment/PaymentSelect"
import {PerksPaymentSelectOnChange, PerksPaymentSelectOption} from "@shared/payments/types"
import detailsToWebAddress from "@irati/checkout/utils/redemption/helpers/detailsToWebAddress"
import history from "@irati/utils/history"
import walletService from "@irati/wallet/graph/wallet"
import {FormattedMessage} from "@irati/utils/lang"
import userService from "@irati/identity/services/users"
import DeliveryAddressForm from "../components/DeliveryAddressForm"
import MerchandiseOrderSummary from "../components/MerchandiseOrderSummary"
import OrderAddressInfo from "../components/OrderAddressInfo"
import {CheckoutContainer, Col, H2} from "./styled"

interface Props {
  deal: Deal
}

enum Step {
  ShippingAddress,
  AcceptTermsAndPay
}

const MerchandiseCheckout: FC<Props> = ({deal}, context) => {
  const [step, setStep] = useState(Step.ShippingAddress)
  const [load, setLoad] = useState<Promise<any>>()
  const [checkoutLoad, setCheckoutLoad] = useState<Promise<any>>()
  const [paymentBreakdown, setPaymentBreakdown] = useState<PerksPaymentSelectOption[]>([])
  const [rewardWallet, setRewardWallet] = useState<Wallet>({})
  const [shippingAddress, setShippingAddress] = useState<Address>({})

  useEffect(() => {
    const user = authentication.user()

    Promise.all([
      userService.regionIdForUser(user.uuid!),
      walletService.get(user.uuid!, PaymentCurrencyType.Rp, WalletEntityType.User, {fetchPolicy: "network-only"})
    ]).then(([{regionId}, wallet]) => {
      setShippingAddress({
        ...shippingAddress,
        state: regionId,
        countryCode: regionId,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        phoneNumber: user.phoneNumber
      })
      setRewardWallet(wallet)
    })
  }, [])

  const nextStep = async() => {
    const request = redemptionFlow.setShippingAddress(shippingAddress)
    setLoad(request)
    await request
    // might track setting shipping address
    setStep(Step.AcceptTermsAndPay)
  }

  const handlePaymentChange: PerksPaymentSelectOnChange = (providers) => {
    setPaymentBreakdown(providers)
  }

  const handlePaymentSelected = async() => {
    const user = authentication.user()
    tracking.redeemPerkStartCheckout(deal)
    const request = checkoutService.startCheckout(user.uuid!).then(async(checkout) => {
      await handleComplete(checkout)
    })
    setCheckoutLoad(request)
  }

  const trackCompleteEvent = (checkout: Checkout) => {
    const {basket} = checkout
    const walletAmount = paymentBreakdown.find((t) => t.type === PaymentProvider.Wallet)?.amount || 0
    tracking.redeemPerkCompleteCheckout(deal, basket!.total!.amount!, basket!.total!.currency!, walletAmount)
  }

  const handleComplete = async(checkout: Checkout) => {
    const details = await redemptionFlow.paymentComplete(deal, checkout.uuid!, checkout.paymentOrder!.uuid!)
    trackCompleteEvent(checkout)
    const url = detailsToWebAddress(details)
    history.replace(url, context)
  }

  const onFieldChange = (updatedAddress: Partial<Address>) => {
    setShippingAddress((current) => {
      return {
        ...current,
        ...updatedAddress
      }
    })
  }

  return (
    <CheckoutContainer>
      <H2 marginBottom={4}>
        <FormattedMessage id="flexiRewards.merchandise.checkout.header" />
      </H2>
      <Row>
        {/* Mobile */}
        <Col md={12} lg={6} lgHidden marginBottom={3}>
          <MerchandiseOrderSummary deal={deal} rewardWallet={rewardWallet} />
        </Col>

        {step === Step.ShippingAddress ? (
          <DeliveryAddressForm onFieldChange={onFieldChange} load={load} shippingAddress={shippingAddress} onClick={nextStep} />
        ) : (
          <>
            <OrderAddressInfo
              onEditClick={() => setStep(Step.ShippingAddress)}
              onPlaceOrderClick={handlePaymentSelected}
              load={checkoutLoad} />
            <PaymentSelect isRewards hideTitle deal={deal} onPaymentChange={handlePaymentChange} />
          </>
        )}

        {/* Desktop */}
        <Col md={12} lg={6} xsHidden smHidden mdHidden>
          <MerchandiseOrderSummary deal={deal} rewardWallet={rewardWallet} />
        </Col>
      </Row>
    </CheckoutContainer>
  )
}

MerchandiseCheckout.contextTypes = {
  appPath: PropTypes.string
}

export default MerchandiseCheckout
